import type { ProductPreviewSupplierNotes } from '@travel-planner/shared/schemas';
import { ExternalLink } from 'lucide-react';
import { sanitizeHtml } from '@/shared/lib';
import { Text } from '@/shared/ui';
import css from './supplier-notes-panel.module.scss';

// Same rule as `PrivateNotesPanel`: `crm.company.notes` is a plain `text`
// column, so a value with no markup keeps its author's line breaks instead of
// being collapsed into one paragraph by the HTML renderer.
const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value);

type SupplierNotesPanelProps = {
  data: ProductPreviewSupplierNotes | undefined;
  isLoading: boolean;
  isError: boolean;
  // Built by the modal from the resolved supplier id, so the record this opens
  // is the record the notes above it were read from. `null` when the caller may
  // not read CRM, or when there is no resolvable supplier to open.
  crmHref: string | null;
};

/**
 * "Supplier CRM" — the CRM-sourced half of the product preview's Private Notes
 * tab, rendered ABOVE the product's own private notes (T-196).
 *
 * Both fields are read live from the supplier record on every open, so one CRM
 * edit reaches every product linked to that supplier and nothing is duplicated
 * onto the products.
 *
 * The section stays visible in every state, including empty and unlinked: a
 * consultant needs to tell "this supplier has no contract year on file" from
 * "this product has no supplier", and a section that disappears answers neither
 * question. Its loading and error states are local, so a CRM failure leaves the
 * product's own notes below it fully readable.
 */
export const SupplierNotesPanel = ({
  data,
  isLoading,
  isError,
  crmHref,
}: SupplierNotesPanelProps) => {
  const notes = data?.notes?.trim() ?? '';
  const resolved = !isLoading && !isError && data;

  return (
    <section aria-labelledby="supplier-notes-heading" className={css.root}>
      <div className={css.head}>
        <div className={css.headText}>
          {/* Visible source label — the section's origin is never carried by
              colour alone. */}
          <span className={css.badge}>Supplier CRM</span>
          <div>
            <h4 className={css.title} id="supplier-notes-heading">
              Supplier information
            </h4>
            <Text as="p" size="xs" variant="muted">
              {data?.supplierName
                ? `Maintained on ${data.supplierName} and shared by every linked product.`
                : 'Maintained on the supplier record and shared by every linked product.'}
            </Text>
          </div>
        </div>
        {/* A plain anchor, not a router Link: the preview and any unsaved
            itinerary work must stay put in this tab. `noopener` stops the new
            tab reaching back into the one holding that work. */}
        {crmHref && (
          <a
            aria-label={`Open supplier CRM for ${
              data?.supplierName || 'this supplier'
            } in a new tab`}
            className={css.crmLink}
            href={crmHref}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open supplier CRM
            <ExternalLink size={13} />
          </a>
        )}
      </div>

      {isLoading && (
        <Text as="p" size="sm" variant="muted">
          Loading supplier information…
        </Text>
      )}

      {/* A CRM read failure is reported in place. It must not take the product's
          own notes down with it, so this returns early only for THIS section. */}
      {!isLoading && isError && (
        <Text as="p" size="sm" variant="error">
          Supplier information could not be loaded.
        </Text>
      )}

      {resolved && !data.linked && (
        <Text as="p" size="sm" variant="muted">
          This product is not linked to a CRM supplier.
        </Text>
      )}

      {resolved && data.unavailable && (
        <Text as="p" size="sm" variant="muted">
          The linked supplier record is unavailable.
        </Text>
      )}

      {resolved && data.linked && !data.unavailable && (
        <dl className={css.fields}>
          <div className={css.field}>
            <dt className={css.label}>Contract Year</dt>
            {/* `—` rather than a hidden row: the absence of a contract year is
                itself information a consultant acts on. */}
            <dd className={css.value}>{data.contractYear ?? '—'}</dd>
          </div>
          <div className={css.field}>
            <dt className={css.label}>Notes</dt>
            <dd className={css.value}>
              {notes ? (
                looksLikeHtml(notes) ? (
                  <div
                    className={css.notes}
                    // Sanitised against the shared allowlist — paragraphs,
                    // lists, links and emphasis survive; scripts, handlers and
                    // unsafe protocols do not.
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(notes) }}
                  />
                ) : (
                  // React escapes this; `white-space: pre-line` in the
                  // stylesheet is what keeps the author's line breaks.
                  <div className={css.notesPlain}>{notes}</div>
                )
              ) : (
                <Text as="span" size="sm" variant="muted">
                  No notes have been added to this supplier in CRM.
                </Text>
              )}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
};
